import {
  FiUsers,
  FiAlertTriangle,
  FiActivity,
  FiRadio,
  FiShield,
  FiChevronDown,
  FiBriefcase,
  FiGlobe,
  FiFlag,
  FiLogOut
} from 'react-icons/fi';

import MetricCard from './MetricCard';
import { buildTerritorialOverview } from '../utils/buildTerritorialOverview';
import { buildNarrativeEcosystem } from '../utils/buildNarrativeEcosystem';

function Dashboard({
  totalActores = 0,
  actoresCriticos = 0,
  alertasActivas = 0,
  totalEventos = 0,
  projectActors = [],
  projectAlerts = [],
  projectSignals = [],
  projectTimeline = [],
  activeFilters = {},
  activeProject,
  projects = [],
  activeProjectId,
  setActiveProjectId,
  logout,
  onOpenReport
}) {
  const totalStakeholders = projectActors.length > 0 ? projectActors.length : totalActores;
  const totalCriticos = projectActors.length > 0
    ? projectActors.filter((actor) =>
        actor.estado === 'Crítico' ||
        actor.riesgo === 'Crítico' ||
        ['Activación Territorial', 'Convergencia Narrativa', 'Escalamiento Territorial']
          .includes(actor._vita?.state?.state)
      ).length
    : actoresCriticos;
  const totalAlertas = projectAlerts.length > 0
    ? projectAlerts.filter((alerta) => alerta.estado !== 'Cerrada').length
    : alertasActivas;
  const totalPulso = projectSignals.length > 0 ? projectSignals.length : totalEventos;

  const overview = buildTerritorialOverview({
    actors: projectActors,
    alerts: projectAlerts,
    signals: projectSignals,
    timeline: projectTimeline,
    filters: activeFilters
  });

  const ecosistema = buildNarrativeEcosystem({
    actors: projectActors,
    alerts: projectAlerts,
    signals: projectSignals,
    timeline: projectTimeline
  });

  // Detectar estado narrativo
  const todasNarrativas = [
    ecosistema.dominante,
    ...ecosistema.secundarias
  ].filter(n => n && n !== 'Sin narrativa territorial detectada');

  const sinDatos = projectActors.length === 0;
  const sinNarrativas = todasNarrativas.length === 0;
  const hayConvergencia = ecosistema.scored &&
    ecosistema.scored.length > 0 &&
    ecosistema.scored[0].fuentes?.size >= 2;

  const estadoNarrativo = sinDatos
    ? 'sin_datos'
    : sinNarrativas
    ? 'sin_narrativas'
    : hayConvergencia
    ? 'relato_emergente'
    : 'en_construccion';

  return (
    <div style={styles.command}>

      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.left}>
          <div style={styles.badge}>
            <FiShield />
            COMMAND CENTER
          </div>
          <h1 style={styles.title}>
            {activeProject?.nombre || 'Centro de Control Territorial'}
          </h1>
          {activeProject && (
            <div style={styles.meta}>
              <div style={styles.metaItem}>
                <FiBriefcase />
                <span>Cliente: {activeProject.cliente || '-'}</span>
              </div>
              <span style={styles.metaDot}>•</span>
              <div style={styles.metaItem}>
                <FiGlobe />
                <span>Región: {activeProject.region || '-'}</span>
              </div>
              <span style={styles.metaDot}>•</span>
              <div style={styles.metaItem}>
                <FiFlag />
                <span>Estado: {activeProject.estado || '-'}</span>
              </div>
            </div>
          )}
        </div>

        <div style={styles.rightPanel}>
          <div style={styles.systemStatusBar}>
            <div style={styles.systemStatus}>
              <div style={styles.systemDot} />
              <span>Sistema territorial activo</span>
            </div>
            <button style={styles.logoutMiniButton} onClick={logout}>
              <FiLogOut />
              Salir
            </button>
          </div>
          {setActiveProjectId ? (
            <div style={styles.selectorCompact}>
              <div style={styles.selectorCompactHeader}>
                <span style={styles.selectorCompactLabel}>Proyecto activo</span>
                <button style={styles.exportMiniButton} onClick={onOpenReport}>
                  Informe ejecutivo
                </button>
              </div>
              <div style={styles.selectWrapper}>
                <select
                  style={styles.projectSelect}
                  value={activeProjectId || ''}
                  onChange={(e) => setActiveProjectId(e.target.value)}
                >
                  <option value="">Seleccionar proyecto</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.nombre || 'Proyecto sin nombre'}
                    </option>
                  ))}
                </select>
                <FiChevronDown style={styles.chevron} />
              </div>
            </div>
          ) : (
            <button style={styles.exportMiniButton} onClick={onOpenReport}>
              Informe ejecutivo
            </button>
          )}
        </div>
      </div>

      {/* BLOQUE TERRITORIAL CENTRAL */}
      <div style={styles.territorialBlock}>

        {/* Estado territorial */}
        <div style={{
          ...styles.estadoBox,
          borderLeftColor: getEstadoColor(overview.estadoTerritorial)
        }}>
          <div style={styles.miniLabel}>ESTADO TERRITORIAL</div>
          <div style={{
            ...styles.estadoValue,
            color: getEstadoColor(overview.estadoTerritorial)
          }}>
            {overview.estadoTerritorial || 'Estable'}
          </div>
          <div style={styles.estadoDesc}>{overview.descripcionEstado}</div>

          {overview.confianza && (
            <div
              style={{
                ...styles.confianzaTag,
                color: getConfianzaColor(overview.confianza.nivel),
                borderColor: getConfianzaColor(overview.confianza.nivel)
              }}
              title={overview.confianza.detalle}
            >
              Confianza cognitiva: {overview.confianza.nivel}
            </div>
          )}
        </div>

        {/* Relato territorial */}
        <div style={styles.relatoBox}>
          <div style={styles.miniLabel}>RELATO TERRITORIAL</div>

          {estadoNarrativo === 'sin_datos' && (
            <div style={styles.relatoVacio}>
              Sin información territorial. Registra stakeholders para activar el análisis.
            </div>
          )}

          {estadoNarrativo === 'sin_narrativas' && (
            <div style={styles.relatoVacio}>
              Sin narrativas declaradas. Agrega lo que escuchas en terreno para construir el relato.
            </div>
          )}

          {estadoNarrativo === 'en_construccion' && (
            <div style={styles.relatoVacio}>
              Relato en construcción. Se requiere convergencia entre múltiples actores para consolidarlo.
            </div>
          )}

          {estadoNarrativo === 'relato_emergente' && (
            <>
              <div style={styles.relatoTexto}>{ecosistema.relato}</div>
              <div style={styles.convergenciaTag}>
                {ecosistema.convergencia}
              </div>
            </>
          )}
        </div>

        {/* Acción sugerida */}
        <div style={styles.accionBox}>
          <div style={styles.miniLabel}>ACCIÓN SUGERIDA</div>
          <div style={styles.accionValue}>{overview.accionSugerida}</div>
        </div>

      </div>

      {/* 4 KPIs */}
      <div style={styles.grid}>
        <MetricCard
          title="Stakeholders monitoreados"
          value={totalStakeholders}
          description="Actores territoriales registrados."
          icon={<FiUsers />}
        />
        <MetricCard
          title="Stakeholders críticos"
          value={totalCriticos}
          description="Actores con sensibilidad territorial."
          icon={<FiActivity />}
          variant="danger"
        />
        <MetricCard
          title="Alertas activas"
          value={totalAlertas}
          description="Eventos que requieren seguimiento."
          icon={<FiAlertTriangle />}
          variant="warning"
        />
        <MetricCard
          title="Pulso territorial"
          value={totalPulso}
          description="Señales sociales y actividad reciente."
          icon={<FiRadio />}
          variant="success"
        />
      </div>

    </div>
  );
}

function getEstadoColor(estado) {
  const colores = {
    'Escalamiento Territorial': '#ef4444',
    'Convergencia Narrativa': '#f97316',
    'Activación Territorial': '#eab308',
    'Sensibilidad Creciente': '#22c55e',
    'Observación': '#06b6d4',
    'Estable': '#64748b'
  };
  return colores[estado] || '#64748b';
}

function getConfianzaColor(nivel) {
  const colores = {
    'Alta': '#22c55e',
    'Media': '#eab308',
    'Baja': '#f97316',
    'Sin datos': '#64748b'
  };
  return colores[nivel] || '#64748b';
}

const styles = {
  command: {
    background: 'radial-gradient(circle at top left,rgba(31,169,122,0.20),transparent 28%), radial-gradient(circle at top right,rgba(9,147,181,0.18),transparent 30%), linear-gradient(135deg,#071a2f 0%,#0b2442 45%,#04101d 100%)',
    borderRadius: '28px',
    padding: '32px',
    marginBottom: '30px',
    boxShadow: '0 24px 55px rgba(0,0,0,0.28)',
    border: '1px solid rgba(255,255,255,0.08)'
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: '28px',
    alignItems: 'start',
    marginBottom: '28px'
  },
  left: { minWidth: 0, paddingTop: '4px' },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    background: 'rgba(255,255,255,0.05)',
    color: 'rgba(32,214,181,0.82)',
    padding: '7px 13px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1px',
    marginBottom: '20px',
    border: '1px solid rgba(255,255,255,0.08)'
  },
  title: {
    margin: 0,
    fontSize: '44px',
    color: 'white',
    letterSpacing: '-1.1px',
    lineHeight: '1.04'
  },
  meta: {
    marginTop: '18px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    color: 'rgba(255,255,255,0.78)',
    fontSize: '14px'
  },
  metaItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  metaDot: { color: '#20d6b5', fontWeight: '900' },
  rightPanel: { display: 'flex', flexDirection: 'column', gap: '14px' },
  systemStatusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '12px'
  },
  systemStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'rgba(255,255,255,0.72)',
    fontSize: '13px',
    fontWeight: '700'
  },
  systemDot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    background: '#16a34a',
    boxShadow: '0 0 0 5px rgba(22,163,74,0.16)'
  },
  logoutMiniButton: {
    background: 'rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.08)',
    padding: '8px 11px',
    borderRadius: '10px',
    fontWeight: '700',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  selectorCompact: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    padding: '13px 15px'
  },
  selectorCompactHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '10px'
  },
  selectorCompactLabel: {
    color: 'rgba(255,255,255,0.66)',
    fontSize: '12px',
    fontWeight: '900'
  },
  exportMiniButton: {
    background: 'rgba(6,182,212,0.1)',
    color: '#67e8f9',
    border: '1px solid rgba(103,232,249,0.22)',
    padding: '7px 10px',
    borderRadius: '10px',
    fontWeight: '900',
    fontSize: '11px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  selectWrapper: { position: 'relative' },
  projectSelect: {
    width: '100%',
    padding: '13px 40px 13px 14px',
    borderRadius: '14px',
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(4,16,29,0.58)',
    color: 'white',
    fontWeight: '900',
    cursor: 'pointer',
    appearance: 'none'
  },
  chevron: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.78)',
    pointerEvents: 'none'
  },
  territorialBlock: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1fr',
    gap: '14px',
    marginBottom: '20px'
  },
  estadoBox: {
    background: 'linear-gradient(180deg,#0b223f,#081b33)',
    borderRadius: '16px',
    padding: '18px',
    borderLeft: '4px solid #64748b'
  },
  miniLabel: {
    color: '#94a3b8',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px',
    fontWeight: '800'
  },
  estadoValue: {
    fontSize: '20px',
    fontWeight: '900',
    marginBottom: '6px',
    lineHeight: '1.2'
  },
  estadoDesc: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '12px',
    lineHeight: '1.5'
  },
  confianzaTag: {
    display: 'inline-block',
    marginTop: '10px',
    padding: '4px 10px',
    borderRadius: '999px',
    border: '1px solid',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.3px',
    cursor: 'help',
    background: 'rgba(255,255,255,0.04)'
  },
  relatoBox: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '18px'
  },
  relatoVacio: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '13px',
    lineHeight: '1.6',
    fontStyle: 'italic'
  },
  relatoEnConstruccion: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '13px',
    marginBottom: '10px'
  },
  relatoTexto: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: '14px',
    lineHeight: '1.6',
    marginBottom: '12px',
    fontStyle: 'italic'
  },
  narrativasPistas: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '10px'
  },
  narrativaPista: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    color: 'rgba(255,255,255,0.65)',
    fontSize: '13px',
    lineHeight: '1.4'
  },
  narrativaDominantePista: {
    color: '#67e8f9',
    fontWeight: '700'
  },
  narrativaPistaDot: {
    color: '#67e8f9',
    fontSize: '14px',
    flexShrink: 0,
    marginTop: '1px'
  },
  relatoNota: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '11px',
    fontStyle: 'italic'
  },
  convergenciaTag: {
    background: 'rgba(103,232,249,0.08)',
    border: '1px solid rgba(103,232,249,0.18)',
    borderRadius: '8px',
    padding: '6px 12px',
    color: '#67e8f9',
    fontSize: '12px',
    fontWeight: '600'
  },
  accionBox: {
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.2)',
    borderRadius: '16px',
    padding: '18px'
  },
  accionValue: {
    color: '#4ade80',
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '1.5'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,minmax(0,1fr))',
    gap: '16px'
  }
};

export default Dashboard;